/**
 * Step 4 — Disclosures.
 *
 * Per `rcp-apply-now-step4-disclosures.html`.
 *
 * A11y:
 *   - PDF preview is served through pdfjs-dist (PLANNED local bundle — see
 *     TODO) with the text-layer ON for SR readability. Until pdfjs-dist is
 *     wired, we render a "Read as text" link beside the iframe.
 *   - "I agree" checkbox is explicitly associated with its `<label>`.
 *   - Time-limited session warning wired via SessionTimeoutModal.
 *
 * NO CDN — the PDF file itself is served from `/portal/documents/:id` (same
 * origin) OR from a local `/assets/disclosures/*.pdf`.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useApplyWizard } from './ApplyWizard';
import { useToast } from '../../components/a11y/AccessibleToast';
import { ArrowLeft, ArrowRight, FileText, ExternalLink } from 'lucide-react';

interface DisclosureDoc {
  id: string;
  title: string;
  href: string;
  textHref: string;
}

const DISCLOSURES: ReadonlyArray<DisclosureDoc> = [
  {
    id: 'tis',
    title: 'Truth in Savings Disclosure',
    href: '/assets/disclosures/tis.pdf',
    textHref: '/assets/disclosures/tis.html',
  },
  {
    id: 'funds-avail',
    title: 'Funds Availability Policy',
    href: '/assets/disclosures/funds-availability.pdf',
    textHref: '/assets/disclosures/funds-availability.html',
  },
  {
    id: 'esign',
    title: 'E-Sign Consent',
    href: '/assets/disclosures/esign.pdf',
    textHref: '/assets/disclosures/esign.html',
  },
];

export function Step4Disclosures(): JSX.Element {
  const { gotoStep } = useApplyWizard();
  const { pushToast } = useToast();
  const navigate = useNavigate();
  const [agreed, setAgreed] = useState(false);
  const [activeDoc, setActiveDoc] = useState<DisclosureDoc>(
    DISCLOSURES[0] as DisclosureDoc,
  );

  function continueNext(): void {
    if (!agreed) {
      pushToast('error', 'Please agree to continue', 'Check the "I&apos;ve read and agree" box to proceed.');
      return;
    }
    navigate('/apply/funding');
  }

  return (
    <section aria-labelledby="step4-heading">
      <h1 id="step4-heading" tabIndex={-1}>Please review these disclosures</h1>
      <p className="lead" style={{ marginBottom: 24 }}>
        You can open each document below, then confirm you&apos;ve read them.
      </p>

      <div className="card">
        <h2>Documents</h2>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }} role="tablist" aria-label="Disclosures">
          {DISCLOSURES.map((d) => {
            const isActive = d.id === activeDoc.id;
            return (
              <li key={d.id} role="presentation">
                <button
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  aria-controls="disclosure-preview"
                  onClick={() => setActiveDoc(d)}
                  className="btn btn-ghost"
                  style={{
                    width: '100%',
                    justifyContent: 'flex-start',
                    gap: 10,
                    marginBottom: 6,
                    borderColor: isActive ? 'var(--pcb-blue)' : 'var(--surface-border)',
                    background: isActive ? 'var(--surface-info-soft)' : 'transparent',
                  }}
                >
                  <FileText aria-hidden="true" size={16} />
                  {d.title}
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      <div
        className="card"
        id="disclosure-preview"
        role="tabpanel"
        aria-labelledby={`tab-${activeDoc.id}`}
        tabIndex={0}
      >
        <h2>{activeDoc.title}</h2>
        <p>
          <a
            href={activeDoc.textHref}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Read ${activeDoc.title} as accessible text — opens in a new tab`}
          >
            <ExternalLink aria-hidden="true" size={14} /> Read as accessible text
          </a>
        </p>
        {/* TODO(pdfjs-dist): swap this <iframe> for a bundled pdfjs-dist viewer
            with text-layer ON. Must remain same-origin; NO CDN. */}
        <iframe
          src={activeDoc.href}
          title={`${activeDoc.title} preview`}
          aria-label={`PDF preview of ${activeDoc.title}`}
          style={{ width: '100%', height: 420, border: '1px solid var(--surface-border)', borderRadius: 6 }}
        />
      </div>

      <div className="card">
        <label style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            aria-describedby="agree-hint"
            style={{ marginTop: 4 }}
          />
          <span>
            <strong>I&apos;ve read the documents above</strong> and agree to the account terms,
            funds-availability policy, and to receive disclosures electronically.
            <span id="agree-hint" className="hint" style={{ display: 'block', marginTop: 6 }}>
              You can print or save any document. We&apos;ll also email you a copy
              after you submit.
            </span>
          </span>
        </label>
      </div>

      <div style={{ display: 'flex', gap: 12, justifyContent: 'space-between' }}>
        <button type="button" className="btn btn-ghost" onClick={() => gotoStep(2)}>
          <ArrowLeft aria-hidden="true" size={16} /> Back
        </button>
        <button type="button" className="btn btn-primary" onClick={continueNext} disabled={!agreed}>
          Continue
          <ArrowRight aria-hidden="true" size={16} />
        </button>
      </div>
    </section>
  );
}
